import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Hammer, Loader2 } from "lucide-react";
import logo from "@/assets/logo-mcm.png";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "operatore" ? "/commesse" : "/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "operatore" ? "/commesse" : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Officina carpenteria metallica"
          className="absolute inset-0 w-full h-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src={logo} alt="MCM Saldature" className="h-14 rounded-lg shadow-lg" data-testid="app-logo" />
          </div>
          <div>
            <h1 className="font-display text-4xl font-bold leading-tight">
              Gestione commesse<br />carpenteria & tubisteria
            </h1>
            <p className="mt-4 text-slate-300 max-w-md">
              Tieni sotto controllo preventivi, costi reali, ore dei dipendenti e
              margini di ogni commessa. Semplice, in italiano, ogni giorno.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-2">
            <img src={logo} alt="MCM Saldature" className="h-10 rounded-md" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-900">Accedi</h2>
            <p className="text-sm text-slate-500 mt-1">Inserisci le tue credenziali aziendali.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@azienda.it"
              required
              data-testid="login-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              data-testid="login-password-input"
            />
          </div>

          {error && (
            <div
              className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="w-full h-11 text-base"
            disabled={loading}
            data-testid="login-submit-button"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Entra"}
          </Button>
        </form>
      </div>
    </div>
  );
}
